import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  ArrowRight,
  Loader2,
  ChevronDown,
  Megaphone,
  CalendarCheck,
  Handshake,
  MessageSquare,
  PenLine,
} from 'lucide-react';
import { TONE_PRESETS } from '@hypema/shared';
import { getCampaign, getCampaigns, createCampaign, updateCampaign } from '@/lib/campaigns';
import { useToast } from '@/hooks/useToast';

const availableTones = TONE_PRESETS.filter((t) => t !== 'casual');

function getGoalTemplates(product: string) {
  const p = product.trim() || 'your product';
  return [
    { icon: Megaphone, label: 'Introduce', desc: `Introduce ${p} to potential customers` },
    { icon: CalendarCheck, label: 'Book demos', desc: `Book a demo call for ${p}` },
    { icon: Handshake, label: 'Partnerships', desc: `Explore partnerships for ${p}` },
    { icon: MessageSquare, label: 'Get feedback', desc: `Get feedback on ${p} from leaders` },
  ];
}

export function CampaignSetupPage() {
  const navigate = useNavigate();
  const params = useParams();
  const toast = useToast();
  const campaignId = params.id as string;
  const isNew = campaignId === 'new';

  const [name, setName] = useState('');
  const [product, setProduct] = useState('');
  const [tone, setTone] = useState<string>('professional');
  const [context, setContext] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [toneOpen, setToneOpen] = useState(false);
  const [customGoal, setCustomGoal] = useState(false);
  const [goalInitialized, setGoalInitialized] = useState(false);
  const toneRef = useRef<HTMLDivElement>(null);

  const productPlaceholder = 'e.g. Financial Consulting, Cybersecurity Audit';

  // Close tone dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (toneRef.current && !toneRef.current.contains(e.target as Node)) {
        setToneOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Load default name for new campaigns
  useEffect(() => {
    if (isNew) {
      getCampaigns().then((campaigns) => {
        setName(`Campaign ${campaigns.length + 1}`);
      }).catch(() => {
        setName('Campaign 1');
      });
    }
  }, [isNew]);

  // Load existing campaign
  useEffect(() => {
    if (!isNew) {
      getCampaign(campaignId).then((campaign) => {
        setName(campaign.name);
        setProduct(campaign.product || '');
        setTone(campaign.tone);
        setContext(campaign.context || campaign.goal || '');
        setLoading(false);
      }).catch(() => {
        toast.error('Campaign not found');
        navigate('/campaign', { replace: true });
      });
    }
  }, [campaignId, isNew]);

  async function handleSave() {
    setSaving(true);
    try {
      if (isNew) {
        const campaign = await createCampaign({ name, product, tone, context });
        navigate(`/campaign/${campaign.id}/leads`, { replace: true });
      } else {
        await updateCampaign(campaignId, { name, product, tone, context });
        navigate(`/campaign/${campaignId}/leads`);
      }
    } catch {
      toast.error('Failed to save campaign');
      setSaving(false);
    }
  }

  const goalTemplates = getGoalTemplates(product);
  const isCustomGoal = context && !goalTemplates.some((g) => g.desc === context);

  // Preselect first goal for new campaigns
  useEffect(() => {
    if (isNew && !goalInitialized && !context) {
      setContext(goalTemplates[0].desc);
      setGoalInitialized(true);
    }
  }, [isNew, goalInitialized, context, goalTemplates]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto w-full animate-fade-in">
      <div>
        <h1 className="text-xl font-bold tracking-tight">
          {isNew ? 'New Campaign' : 'Campaign Setup'}
        </h1>
        <p className="text-sm text-base-content/40 mt-1">
          Define what you&apos;re promoting, choose a tone, and set your goal.
        </p>
      </div>

      <div className="glass-card rounded-xl p-5 space-y-5">
        {/* Campaign name */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-base-content/40 mb-2 block">
            Campaign Name
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Q1 SaaS Founders Outreach"
            className="input input-bordered w-full"
            autoFocus
          />
        </div>

        {/* Product */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-base-content/40 mb-2 block">
            Product
          </label>
          <input
            type="text"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            placeholder={productPlaceholder}
            className="input input-bordered w-full"
          />
          <p className="text-[10px] text-base-content/30 mt-1">
            Referenced in your emails and goal templates below.
          </p>
        </div>

        {/* Tone — custom dropdown */}
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold uppercase tracking-wider text-base-content/40 shrink-0">
            Tone
          </label>
          <div className="relative" ref={toneRef}>
            <button
              onClick={() => setToneOpen(!toneOpen)}
              className="inline-flex items-center gap-1 text-sm font-medium text-primary capitalize cursor-pointer"
            >
              {tone}
              <ChevronDown size={10} className={`transition-transform ${toneOpen ? 'rotate-180' : ''}`} />
            </button>
            {toneOpen && (
              <div className="absolute top-full left-0 mt-1 py-1 rounded-lg bg-base-200 border border-base-300/30 shadow-lg z-50 min-w-[140px] animate-fade-in">
                {availableTones.map((t) => (
                  <button
                    key={t}
                    onClick={() => { setTone(t); setToneOpen(false); }}
                    className={`w-full px-3 py-1.5 text-left text-sm capitalize transition-colors ${
                      tone === t
                        ? 'text-primary font-medium'
                        : 'text-base-content/60 hover:text-base-content hover:bg-base-300/20'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Goal — icon cards */}
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider text-base-content/40 mb-2 block">
            Goal
          </label>
          <div className="grid grid-cols-2 gap-2">
            {goalTemplates.map((goal) => {
              const selected = context === goal.desc;
              return (
                <button
                  key={goal.label}
                  onClick={() => { setContext(goal.desc); setCustomGoal(false); }}
                  className={`flex items-start gap-2.5 p-3 rounded-xl text-left transition-all ${
                    selected
                      ? 'bg-primary/10 border border-primary/25 shadow-sm'
                      : 'border border-base-300/20 hover:border-base-300/40'
                  }`}
                >
                  <goal.icon
                    size={16}
                    className={`shrink-0 mt-0.5 ${selected ? 'text-primary' : 'text-base-content/25'}`}
                  />
                  <div>
                    <p className={`text-xs font-medium ${selected ? 'text-primary' : 'text-base-content/70'}`}>
                      {goal.label}
                    </p>
                    <p className={`text-[10px] leading-tight mt-0.5 ${selected ? 'text-primary/60' : 'text-base-content/35'}`}>
                      {goal.desc}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
          {/* Custom goal */}
          <button
            onClick={() => { setCustomGoal(true); setContext(''); }}
            className={`mt-2 flex items-start gap-2.5 p-3 rounded-xl w-full text-left transition-all ${
              customGoal || isCustomGoal
                ? 'bg-primary/10 border border-primary/25 shadow-sm'
                : 'border border-base-300/20 hover:border-base-300/40'
            }`}
          >
            <PenLine
              size={16}
              className={`shrink-0 mt-0.5 ${customGoal || isCustomGoal ? 'text-primary' : 'text-base-content/25'}`}
            />
            <div>
              <p className={`text-xs font-medium ${customGoal || isCustomGoal ? 'text-primary' : 'text-base-content/70'}`}>
                Custom goal
              </p>
              <p className={`text-[10px] leading-tight mt-0.5 ${customGoal || isCustomGoal ? 'text-primary/60' : 'text-base-content/35'}`}>
                Describe your own campaign objective
              </p>
            </div>
          </button>
          {(customGoal || isCustomGoal) && (
            <input
              type="text"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="Describe your campaign goal..."
              className="input input-bordered input-sm w-full mt-2 animate-fade-in"
              autoFocus
            />
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn btn-primary gap-2"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : null}
          {isNew ? 'Create & Select Leads' : 'Save & Next'} <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}
